
import React, { useState, useEffect } from 'react';
import { Company, Order, OrderStatus, PaymentStatus, PaymentRecord, ClearingInfo, BitumenGrade } from '../types';
import { Plus, Trash2, Save, X, DollarSign, Package, Ship, Calendar, User, FileText, Mail, Phone, MapPin } from 'lucide-react';

interface PartySectionProps {
  title: string;
  name: string;
  setName: (val: string) => void;
  email: string;
  setEmail: (val: string) => void;
  phone: string;
  setPhone: (val: string) => void;
  address: string;
  setAddress: (val: string) => void;
  placeholder: string;
}

const PartySection: React.FC<PartySectionProps> = ({ 
  title, name, setName, email, setEmail, phone, setPhone, address, setAddress, placeholder 
}) => (
  <div className="bg-slate-50/50 p-6 rounded-2xl border border-slate-100 space-y-4">
    <h4 className="text-[10px] font-black text-slate-400 uppercase tracking-widest flex items-center gap-2">
      <User size={12} className="text-indigo-400" /> {title}
    </h4>
    <div className="space-y-3">
      <input
        required
        type="text"
        value={name}
        onChange={e => setName(e.target.value.toUpperCase())}
        className="w-full px-4 py-2.5 bg-white border border-slate-200 rounded-xl focus:ring-4 focus:ring-indigo-500/10 outline-none font-bold text-sm"
        placeholder={placeholder}
      />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div className="relative">
          <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-300" size={14} />
          <input
            type="email"
            value={email}
            onChange={e => setEmail(e.target.value)}
            className="w-full pl-9 pr-4 py-2 bg-white border border-slate-200 rounded-xl text-xs outline-none focus:border-indigo-400"
            placeholder="Email Address"
          />
        </div>
        <div className="relative">
          <Phone className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-300" size={14} />
          <input
            type="tel"
            value={phone}
            onChange={e => setPhone(e.target.value)}
            className="w-full pl-9 pr-4 py-2 bg-white border border-slate-200 rounded-xl text-xs outline-none focus:border-indigo-400"
            placeholder="Telephone Number"
          />
        </div>
      </div>
      <div className="relative">
        <MapPin className="absolute left-3 top-3 text-slate-300" size={14} />
        <textarea
          value={address}
          onChange={e => setAddress(e.target.value)}
          className="w-full pl-9 pr-4 py-2 bg-white border border-slate-200 rounded-xl text-xs outline-none focus:border-indigo-400 h-20 resize-none"
          placeholder="Full Physical / Postal Address"
        />
      </div>
    </div>
  </div>
);

interface NewOrderFormProps {
  activeCompany: Company | 'ALL';
  onSubmit: (order: Order) => void;
  onCancel: () => void;
  initialData?: Order;
}

export const NewOrderForm: React.FC<NewOrderFormProps> = ({ activeCompany, onSubmit, onCancel, initialData }) => {
  const [formCompany, setFormCompany] = useState<Company>(
    initialData?.company || (activeCompany === 'ALL' ? Company.SHIV : activeCompany)
  );

  // General
  const [orderId, setOrderId] = useState(initialData?.id || '');
  const [orderDate, setOrderDate] = useState(initialData?.orderDate || new Date().toISOString().split('T')[0]);
  
  // Consignee
  const [customerName, setCustomerName] = useState(initialData?.customerName || '');
  const [consigneeEmail, setConsigneeEmail] = useState(initialData?.consigneeEmail || '');
  const [consigneePhone, setConsigneePhone] = useState(initialData?.consigneePhone || '');
  const [consigneeAddress, setConsigneeAddress] = useState(initialData?.consigneeAddress || '');

  // Supplier
  const [supplier, setSupplier] = useState(initialData?.supplier || '');
  const [supplierEmail, setSupplierEmail] = useState(initialData?.supplierEmail || '');
  const [supplierPhone, setSupplierPhone] = useState(initialData?.supplierPhone || '');
  const [supplierAddress, setSupplierAddress] = useState(initialData?.supplierAddress || '');

  // Shipper
  const [shipper, setShipper] = useState(initialData?.shipper || '');
  const [shipperEmail, setShipperEmail] = useState(initialData?.shipperEmail || '');
  const [shipperPhone, setShipperPhone] = useState(initialData?.shipperPhone || '');
  const [shipperAddress, setShipperAddress] = useState(initialData?.shipperAddress || '');

  // Shipping / Logistics
  const [portOfDestination, setPortOfDestination] = useState(initialData?.portOfDestination || '');
  const [vesselPlanned, setVesselPlanned] = useState(initialData?.vesselPlanned || '');
  const [ets, setEts] = useState(initialData?.ets || '');
  const [eta, setEta] = useState(initialData?.eta || '');
  const [blNumber, setBlNumber] = useState(initialData?.blNumber || '');

  // Items
  const [items, setItems] = useState(initialData?.items.map(i => ({ ...i })) || [{ id: Date.now().toString(), name: '', quantity: 0, unitPrice: 0 }]);
  
  // Financials - Advance Logic
  const initialAdvance = initialData?.financials.paymentHistory.find(p => p.type === 'Advance')?.amount.toString() || '';
  const [advanceAmount, setAdvanceAmount] = useState<string>(initialAdvance);
  const [advanceDate, setAdvanceDate] = useState(initialData?.financials.paymentHistory.find(p => p.type === 'Advance')?.date || '');

  // Notes
  const [notes, setNotes] = useState(initialData?.notes || '');

  const calculateTotal = () => items.reduce((sum, item) => sum + (item.quantity * item.unitPrice), 0);
  const totalAmount = calculateTotal();
  const numAdvance = parseFloat(advanceAmount) || 0;
  
  const balance = totalAmount - numAdvance;

  const handleAddItem = () => {
    setItems([...items, { id: Math.random().toString(36).substr(2, 9), name: '', quantity: 0, unitPrice: 0 }]);
  };

  const handleRemoveItem = (id: string) => {
    if (items.length > 1) {
      setItems(items.filter(item => item.id !== id));
    }
  };

  const handleItemChange = (id: string, field: string, value: string | number) => {
    setItems(items.map(item => 
      item.id === id ? { ...item, [field]: value } : item
    ));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    const paymentStatus = numAdvance >= totalAmount 
      ? PaymentStatus.PAID 
      : numAdvance > 0 
        ? PaymentStatus.PARTIAL 
        : PaymentStatus.PENDING;

    let paymentHistory: PaymentRecord[] = initialData?.financials.paymentHistory ? [...initialData.financials.paymentHistory] : [];
    
    const advanceIdx = paymentHistory.findIndex(p => p.type === 'Advance');
    if (numAdvance > 0) {
      const record = {
        id: advanceIdx >= 0 ? paymentHistory[advanceIdx].id : `pay-${Date.now()}`,
        date: advanceDate || orderDate,
        amount: numAdvance,
        type: 'Advance' as const,
        note: 'Initial advance payment'
      };
      if (advanceIdx >= 0) paymentHistory[advanceIdx] = record;
      else paymentHistory.push(record);
    } else if (advanceIdx >= 0) {
      paymentHistory.splice(advanceIdx, 1);
    }

    let finalOrderId = orderId;
    if (!finalOrderId) {
      let prefix = 'SI';
      if (formCompany === Company.KAILASA) prefix = 'KI';
      else if (formCompany === Company.SHIV_TZ) prefix = 'ST';
      const year = new Date().getFullYear();
      const sequence = Math.floor(1000 + Math.random() * 9000);
      finalOrderId = `${prefix}-${year}-${sequence}`;
    }

    const defaultClearing: ClearingInfo = initialData?.clearingInfo || {
      agentName: '',
      documentsSent: false,
      status: 'NOT_STARTED',
      costs: [
        { id: `c-${Date.now()}-1`, name: 'Taxes', amount: 0, status: 'PENDING' },
        { id: `c-${Date.now()}-2`, name: 'Wharfage', amount: 0, status: 'PENDING' },
        { id: `c-${Date.now()}-3`, name: 'Shipping Line', amount: 0, status: 'PENDING' },
      ]
    };

    const newOrder: Order = {
      ...initialData,
      id: finalOrderId,
      orderDate,
      customerName,
      consigneeEmail,
      consigneePhone,
      consigneeAddress,
      supplier,
      supplierEmail,
      supplierPhone,
      supplierAddress,
      shipper,
      shipperEmail,
      shipperPhone,
      shipperAddress,
      portOfDestination,
      vesselPlanned,
      ets,
      eta,
      blNumber,
      company: formCompany,
      items: items.map((item, index) => ({
        id: item.id || `${index + 1}`,
        name: item.name,
        quantity: item.quantity,
        unitPrice: item.unitPrice
      })),
      financials: {
        totalAmount,
        currency: initialData?.financials.currency || 'USD',
        amountPaid: paymentHistory.reduce((sum, p) => sum + p.amount, 0),
        balanceAmount: totalAmount - paymentHistory.reduce((sum, p) => sum + p.amount, 0),
        paymentStatus,
        paymentHistory
      },
      currentStatus: initialData?.currentStatus || OrderStatus.PLACED,
      timeline: initialData?.timeline || [{ status: OrderStatus.PLACED, date: orderDate, note: `Order ${finalOrderId} placed.` }],
      documents: initialData?.documents || [],
      notes: notes || undefined,
      clearingInfo: defaultClearing
    };

    onSubmit(newOrder);
  };

  return (
    <div className="max-w-6xl mx-auto animate-fade-in pb-12">
      <div className="flex items-center justify-between mb-6 px-4">
        <div>
          <h2 className="text-2xl font-black text-slate-800 tracking-tight">
            {initialData ? 'Modify Order Registry' : 'Provision New Order'}
          </h2>
          <p className="text-slate-500 text-sm font-medium">
            {initialData ? `Updating operational record for ${initialData.id}` : 'Capture comprehensive logistical profiles for the operational chain.'}
          </p>
        </div>
        <button onClick={onCancel} className="p-3 hover:bg-slate-100 rounded-2xl transition-all text-slate-400">
          <X className="w-6 h-6" />
        </button>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">

        {activeCompany === 'ALL' && (
          <div className="mx-4 bg-amber-50 border border-amber-100 p-6 rounded-3xl shadow-sm">
            <label className="block text-[10px] font-black text-amber-700 uppercase tracking-widest mb-3">Operating Entity</label>
            <select
              value={formCompany}
              onChange={(e) => setFormCompany(e.target.value as Company)}
              className="w-full md:w-1/2 px-4 py-3 border border-amber-200 rounded-xl focus:ring-4 focus:ring-amber-500/10 bg-white text-slate-800 font-bold outline-none"
            >
              <option value={Company.SHIV}>Shiv International Ltd (Uganda)</option>
              <option value={Company.KAILASA}>Kailasa International Ltd (Kenya)</option>
              <option value={Company.SHIV_TZ}>Shiv International Ltd (Tanzania)</option>
            </select>
          </div>
        )}
        
        <div className="mx-4 bg-white p-8 rounded-[32px] border border-slate-200 shadow-sm">
          <h3 className="text-sm font-black text-slate-800 mb-6 flex items-center gap-2 uppercase tracking-widest">
            <Calendar className="w-4 h-4 text-indigo-500" />
            Order Details
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
             <div className="space-y-1">
              <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest px-1">Order Number (Manual Override)</label>
              <input
                type="text"
                readOnly={!!initialData}
                value={orderId}
                onChange={e => setOrderId(e.target.value)}
                className={`w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-2xl focus:ring-4 focus:ring-indigo-500/10 outline-none transition-all font-mono ${initialData ? 'opacity-60 cursor-not-allowed' : ''}`}
                placeholder="LEAVE EMPTY FOR AUTO-GEN"
              />
            </div>
            <div className="space-y-1">
              <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest px-1">Order Date</label>
              <input
                required
                type="date"
                value={orderDate}
                onChange={e => setOrderDate(e.target.value)}
                className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-2xl focus:ring-4 focus:ring-indigo-500/10 outline-none font-bold"
              />
            </div>
          </div>
        </div>

        <div className="mx-4 bg-white p-8 rounded-[32px] border border-slate-200 shadow-sm">
          <h3 className="text-sm font-black text-slate-800 mb-6 flex items-center gap-2 uppercase tracking-widest">
            <User className="w-4 h-4 text-indigo-500" />
            Operational Parties
          </h3>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <PartySection 
              title="Supplier Details" 
              name={supplier} setName={setSupplier}
              email={supplierEmail} setEmail={setSupplierEmail}
              phone={supplierPhone} setPhone={setSupplierPhone}
              address={supplierAddress} setAddress={setSupplierAddress}
              placeholder="Vendor / Manufacturer Name"
            />
            <PartySection 
              title="Shipper / Logistics" 
              name={shipper} setName={setShipper}
              email={shipperEmail} setEmail={setShipperEmail}
              phone={shipperPhone} setPhone={setShipperPhone}
              address={shipperAddress} setAddress={setShipperAddress}
              placeholder="Shipper Name"
            />
            <PartySection 
              title="Consignee Details" 
              name={customerName} setName={setCustomerName}
              email={consigneeEmail} setEmail={setConsigneeEmail}
              phone={consigneePhone} setPhone={setConsigneePhone}
              address={consigneeAddress} setAddress={setConsigneeAddress}
              placeholder="Recipient Entity Name"
            />
          </div>
        </div>

        <div className="mx-4 bg-white p-8 rounded-[32px] border border-slate-200 shadow-sm">
          <div className="flex justify-between items-center mb-8">
            <h3 className="text-sm font-black text-slate-800 flex items-center gap-2 uppercase tracking-widest">
              <Package className="w-4 h-4 text-indigo-500" />
              Product Details
            </h3>
            <button
              type="button"
              onClick={handleAddItem}
              className="text-[10px] font-black uppercase text-indigo-600 bg-indigo-50 px-4 py-2 rounded-xl border border-indigo-100 hover:bg-indigo-100 transition-all flex items-center gap-2"
            >
              <Plus className="w-4 h-4" /> Add Line
            </button>
          </div>
          
          <div className="space-y-4">
            {items.map((item) => (
              <div key={item.id} className="grid grid-cols-1 md:grid-cols-12 gap-4 items-end bg-slate-50/30 p-4 rounded-2xl border border-slate-100 group">
                <div className="md:col-span-6">
                  <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1.5 block">Description</label>
                   <input
                    required
                    type="text"
                    list="bitumen-grades-list"
                    placeholder="E.G. BITUMEN 60/70"
                    value={item.name}
                    onChange={e => handleItemChange(item.id, 'name', e.target.value.toUpperCase())}
                    className="w-full px-4 py-3 bg-white border border-slate-200 rounded-xl focus:ring-4 focus:ring-indigo-500/10 outline-none font-bold text-sm"
                  />
                </div>
                <div className="md:col-span-2">
                   <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1.5 block">Quantity (MT)</label>
                   <input
                    required
                    type="number"
                    step="0.001"
                    value={item.quantity === 0 ? '' : item.quantity}
                    onChange={e => handleItemChange(item.id, 'quantity', parseFloat(e.target.value) || 0)}
                    className="w-full px-4 py-3 bg-white border border-slate-200 rounded-xl focus:ring-4 focus:ring-indigo-500/10 outline-none font-black text-sm"
                  />
                </div>
                <div className="md:col-span-2">
                   <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1.5 block">Unit Price ($)</label>
                   <input
                    required
                    type="number"
                    step="0.01"
                    value={item.unitPrice === 0 ? '' : item.unitPrice}
                    onChange={e => handleItemChange(item.id, 'unitPrice', parseFloat(e.target.value) || 0)}
                    className="w-full px-4 py-3 bg-white border border-slate-200 rounded-xl focus:ring-4 focus:ring-indigo-500/10 outline-none font-black text-sm"
                  />
                </div>
                <div className="md:col-span-2 flex justify-end pb-1">
                    <button
                    type="button"
                    onClick={() => handleRemoveItem(item.id)}
                    className={`p-2.5 rounded-xl border transition-all ${items.length === 1 ? 'border-transparent text-transparent pointer-events-none' : 'text-slate-300 hover:text-rose-500 hover:bg-rose-50 border-slate-100 hover:border-rose-100'}`}
                    >
                    <Trash2 size={18} />
                    </button>
                </div>
              </div>
            ))}
          </div>

          <datalist id="bitumen-grades-list">
            {Object.values(BitumenGrade).filter(g => g !== BitumenGrade.UNKNOWN).map(grade => (
              <option key={grade} value={grade} />
            ))}
            <option value="PG 82-22" />
            <option value="Emulsion C55" />
          </datalist>

          <div className="mt-8 pt-6 border-t border-slate-100 flex justify-end items-center">
            <div className="text-right">
              <p className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] mb-1">Valuation (USD)</p>
              <p className="text-3xl font-black text-slate-900">${totalAmount.toLocaleString()}</p>
            </div>
          </div>
        </div>

        <div className="mx-4 bg-white p-8 rounded-[32px] border border-slate-200 shadow-sm">
          <h3 className="text-sm font-black text-slate-800 mb-6 flex items-center gap-2 uppercase tracking-widest">
            <Ship className="w-4 h-4 text-indigo-500" />
            Shipment Details
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
             <div className="space-y-1">
              <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest px-1">Discharge Port</label>
              <input
                required
                type="text"
                value={portOfDestination}
                onChange={e => setPortOfDestination(e.target.value.toUpperCase())}
                className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-2xl focus:ring-4 focus:ring-indigo-500/10 outline-none font-bold text-xs"
                placeholder="E.G. DAR ES SALAAM"
              />
            </div>
            <div className="space-y-1">
              <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest px-1">Planned Vessel</label>
              <input
                type="text"
                value={vesselPlanned}
                onChange={e => setVesselPlanned(e.target.value.toUpperCase())}
                className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-2xl focus:ring-4 focus:ring-indigo-500/10 outline-none font-bold text-xs"
                placeholder="Vessel Name"
              />
            </div>
            <div className="space-y-1">
              <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest px-1">ETS (Sailing)</label>
              <input type="date" value={ets} onChange={e => setEts(e.target.value)} className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-2xl text-xs font-bold" />
            </div>
            <div className="space-y-1">
              <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest px-1">ETA (Arrival)</label>
              <input type="date" value={eta} onChange={e => setEta(e.target.value)} className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-2xl text-xs font-bold" />
            </div>
             <div className="space-y-1">
              <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest px-1">BL Number</label>
              <input
                type="text"
                value={blNumber}
                onChange={e => setBlNumber(e.target.value.toUpperCase())}
                className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-2xl focus:ring-4 focus:ring-indigo-500/10 outline-none font-mono font-bold text-xs"
                placeholder="Master B/L"
              />
            </div>
          </div>
        </div>

        <div className="mx-4 grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-white p-8 rounded-[32px] border border-slate-200 shadow-sm">
                <h3 className="text-sm font-black text-slate-800 mb-6 flex items-center gap-2 uppercase tracking-widest">
                    <DollarSign className="w-4 h-4 text-indigo-500" />
                    Payment Details
                </h3>
                <div className="grid grid-cols-2 gap-6">
                    <div className="space-y-1">
                        <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest px-1">Deposit ($)</label>
                        <input
                            type="number"
                            min="0"
                            step="0.01"
                            value={advanceAmount}
                            onChange={e => setAdvanceAmount(e.target.value)}
                            className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-2xl focus:ring-4 focus:ring-indigo-500/10 outline-none font-black"
                            placeholder="0.00"
                        />
                    </div>
                    <div className="space-y-1">
                        <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest px-1">Payment Date</label>
                        <input
                            type="date"
                            disabled={!advanceAmount || parseFloat(advanceAmount) <= 0}
                            value={advanceDate}
                            onChange={e => setAdvanceDate(e.target.value)}
                            className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-2xl focus:ring-4 focus:ring-indigo-500/10 outline-none disabled:opacity-40 font-bold"
                        />
                    </div>
                </div>
                <div className="mt-8 bg-slate-900 px-6 py-4 rounded-2xl flex items-center justify-between">
                    <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Outstanding Payables</span>
                    <span className={`text-xl font-black ${balance > 0 ? 'text-rose-400' : 'text-emerald-400'}`}>${balance.toLocaleString()}</span>
                </div>
            </div>

            <div className="bg-white p-8 rounded-[32px] border border-slate-200 shadow-sm">
                <h3 className="text-sm font-black text-slate-800 mb-6 flex items-center gap-2 uppercase tracking-widest">
                    <FileText className="w-4 h-4 text-indigo-500" />
                    Additional Notes
                </h3>
                <textarea
                    value={notes}
                    onChange={e => setNotes(e.target.value)}
                    className="w-full px-5 py-4 bg-slate-50 border border-slate-200 rounded-2xl focus:ring-4 focus:ring-indigo-500/10 outline-none h-32 resize-none text-sm font-medium leading-relaxed"
                    placeholder="Terms, conditions, or site instructions..."
                />
            </div>
        </div>

        <div className="mx-4 flex flex-col md:flex-row items-center gap-4 pt-4 pb-12">
          <button
            type="button"
            onClick={onCancel}
            className="w-full md:w-48 py-4 px-8 bg-white border border-slate-200 text-slate-500 font-black text-[10px] uppercase tracking-widest rounded-2xl hover:bg-slate-50 transition-all active:scale-95"
          >
            Cancel
          </button>
          <button
            type="submit"
            className="flex-1 w-full py-4 px-8 bg-indigo-600 text-white font-black text-[10px] uppercase tracking-[0.3em] rounded-2xl hover:bg-indigo-700 transition-all flex items-center justify-center gap-3 shadow-2xl shadow-indigo-100 active:scale-[0.99]"
          >
            <Save className="w-5 h-5" />
            {initialData ? 'Update Registry' : 'Post Record'}
          </button>
        </div>
      </form>
    </div>
  );
};
